import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  CardActions, 
  Divider, 
  Typography, 
  Button, 
  Avatar, 
  Chip,
  Fade
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Security as SecurityIcon, 
  Edit as EditIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import TwoFactorSetup from './TwoFactorSetup';

export default function ProfileCard() {
  const user = useSelector(selectUser);
  const [open2FA, setOpen2FA] = useState(false);
  
  if (!user) {
    return null;
  }

  return (
    <Fade in timeout={800}>
      <Card
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          }
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 3,
            pb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '50px',
              background: 'white',
              borderRadius: '50% 50% 0 0 / 20px 20px 0 0',
            }
          }}
        />
        
        <CardContent sx={{ pt: 0, px: 3, pb: 3, position: 'relative', mt: -5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar 
              sx={{ 
                width: 100, 
                height: 100, 
                mr: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                border: '4px solid white',
              }}
            >
              <PersonIcon sx={{ fontSize: 50 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {user.fullName}
                {user.isTwoFactorEnabled && (
                  <VerifiedUserIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                )}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                @{user.username}
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 1.5, color: 'action.active' }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {user.email}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <VerifiedUserIcon sx={{ mr: 1.5, color: 'action.active' }} />
              <Typography variant="body1" sx={{ mr: 1, fontWeight: 500 }}>
                Роли:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {user.roles.map((role) => (
                  <Chip 
                    key={role.id} 
                    label={role.name} 
                    size="small" 
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShieldIcon sx={{ mr: 1.5, color: 'action.active' }} />
              <Typography variant="body1" sx={{ mr: 1, fontWeight: 500 }}>
                Двухфакторная аутентификация:
              </Typography>
              <Chip 
                label={user.isTwoFactorEnabled ? 'Включена' : 'Отключена'} 
                size="small" 
                icon={user.isTwoFactorEnabled ? <VerifiedUserIcon /> : undefined}
                sx={{
                  bgcolor: user.isTwoFactorEnabled ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0,0,0,0.08)',
                  color: user.isTwoFactorEnabled ? '#4caf50' : 'text.secondary',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: '#4caf50'
                  }
                }}
              />
            </Box>
          </Box>
        </CardContent>
        
        <Divider />
        
        <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button
            startIcon={<SecurityIcon />}
            variant={user.isTwoFactorEnabled ? 'outlined' : 'contained'}
            color={user.isTwoFactorEnabled ? 'error' : 'primary'}
            onClick={() => setOpen2FA(true)}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              ...(user.isTwoFactorEnabled ? {} : {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                }
              })
            }}
          >
            {user.isTwoFactorEnabled ? 'Отключить 2FA' : 'Включить 2FA'}
          </Button>
        </CardActions>
        
        <TwoFactorSetup 
          open={open2FA} 
          onClose={() => setOpen2FA(false)} 
          isEnabled={user.isTwoFactorEnabled} 
        />
      </Card>
    </Fade>
  );
}